import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Mail, Lock, Smartphone, Gift, ShieldCheck, ArrowLeft, Eye, EyeOff,
  MessageSquare, KeyRound, RotateCcw, Check, IdCard, CalendarDays,
} from 'lucide-react'
import { Logo } from '../components/Layout'
import { Button, Field } from '../components/UI'
import { useAuth, toast } from '../store'
import { get, post } from '../lib/api'
import { fa, toEnDigits } from '../lib/format'

type Cfg = {
  registrationOpen: boolean; passwordLogin: boolean; otpLogin: boolean
  identityRequired?: boolean; birthDateRequired?: boolean
  smsReady: boolean; requireMobileVerify: boolean
  otpLength: number; otpTtl: number; otpCooldown: number; sandbox: boolean
}

const DEFAULT_CFG: Cfg = {
  registrationOpen: true, passwordLogin: true, otpLogin: false, smsReady: false,
  identityRequired: false, birthDateRequired: true,
  requireMobileVerify: false, otpLength: 5, otpTtl: 3, otpCooldown: 90, sandbox: false,
}

/* ─────────────────────────────────────────── ورودی کد پیامکی */
function CodeInput({ length, value, onChange, onComplete }: {
  length: number; value: string; onChange: (v: string) => void; onComplete?: (v: string) => void
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  const setAt = (i: number, ch: string) => {
    const next = digits.map((d, j) => (j === i ? ch : d)).join('').replace(/ /g, '')
    onChange(next)
    if (ch && i < length - 1) refs.current[i + 1]?.focus()
    if (next.length === length) onComplete?.(next)
  }

  return (
    <div className="flex justify-center gap-2" dir="ltr">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el }}
          className="input num h-12 w-11 !px-0 text-center text-lg font-bold"
          inputMode="numeric" maxLength={1}
          value={digits[i]?.trim() || ''}
          onChange={(e) => setAt(i, toEnDigits(e.target.value).replace(/\D/g, '').slice(-1))}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i]?.trim() && i > 0) refs.current[i - 1]?.focus()
          }}
          onPaste={(e) => {
            e.preventDefault()
            const t = toEnDigits(e.clipboardData.getData('text')).replace(/\D/g, '').slice(0, length)
            onChange(t)
            if (t.length === length) onComplete?.(t)
          }}
        />
      ))}
    </div>
  )
}

function Countdown({ seconds, onDone }: { seconds: number; onDone: () => void }) {
  const [left, setLeft] = useState(seconds)
  useEffect(() => { setLeft(seconds) }, [seconds])
  useEffect(() => {
    if (left <= 0) { onDone(); return }
    const id = setTimeout(() => setLeft((l) => l - 1), 1000)
    return () => clearTimeout(id)
  }, [left])
  const m = Math.floor(left / 60), s = left % 60
  return <span className="num">{fa(m)}:{fa(String(s).padStart(2, '0'))}</span>
}

/* ─────────────────────────────────────────── صفحه ورود / ثبت‌نام */
export default function Auth({ mode }: { mode: 'login' | 'register' }) {
  const { login, register, adopt, user } = useAuth()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [cfg, setCfg] = useState<Cfg>(DEFAULT_CFG)

  // روش انتخاب‌شده: password | otp
  const [method, setMethod] = useState<'password' | 'otp'>('password')
  // مرحله: form → code (کد پیامکی) → finish (فقط ثبت‌نام با کد)
  const [step, setStep] = useState<'form' | 'code' | 'finish'>('form')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mobile, setMobile] = useState('')
  const [ref, setRef] = useState(params.get('ref') ?? '')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [nationalId, setNationalId] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [otp, setOtp] = useState('')          // کد اپلیکیشن (TOTP)
  const [code, setCode] = useState('')        // کد پیامکی
  const [ticket, setTicket] = useState('')
  const [needOtp, setNeedOtp] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agree, setAgree] = useState(mode === 'login')

  useEffect(() => { if (user) nav(params.get('next') || '/dashboard', { replace: true }) }, [user])

  useEffect(() => {
    get<Cfg>('/auth/config').then((c) => {
      setCfg(c)
      // اگر ورود با رمز خاموش است، مستقیم به روش پیامکی برو
      if (!c.passwordLogin && c.otpLogin) setMethod('otp')
      if (mode === 'register' && c.requireMobileVerify) setMethod('otp')
    }).catch(() => {})
  }, [mode])

  const smsMode = method === 'otp' && cfg.smsReady

  const reset = () => { setStep('form'); setCode(''); setTicket('') }

  /* ---------------- درخواست کد ---------------- */
  const requestCode = async () => {
    const num = toEnDigits(mobile).trim()
    if (!num) return toast.err('شماره موبایل را وارد کنید')
    if (mode === 'register' && !agree) return toast.err('پذیرش قوانین الزامی است')
    setLoading(true)
    try {
      const r: any = await post('/auth/otp/request', {
        mobile: num, purpose: mode === 'register' ? 'register' : 'login',
      })
      setCooldown(r.cooldown || cfg.otpCooldown)
      setStep('code')
      setCode('')
      toast.ok(`کد تأیید به ${r.mobile} پیامک شد`)
      if (r.debugCode) toast.ok(`حالت آزمایشی — کد: ${r.debugCode}`)
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  /* ---------------- تأیید کد ---------------- */
  const verifyCode = async (value?: string) => {
    const c = value || code
    if (c.length < cfg.otpLength) return
    setLoading(true)
    try {
      const num = toEnDigits(mobile).trim()
      if (mode === 'login') {
        const r: any = await post('/auth/otp/verify', { mobile: num, code: c, purpose: 'login' })
        adopt(r.token, r.user)
        toast.ok('خوش آمدید!')
        nav(params.get('next') || '/dashboard', { replace: true })
      } else {
        const r: any = await post('/auth/otp/verify', { mobile: num, code: c, purpose: 'register' })
        setTicket(r.ticket)
        setStep('finish')
        toast.ok('شماره شما تأیید شد')
      }
    } catch (e: any) { toast.err(e.message); setCode('') } finally { setLoading(false) }
  }

  /* ---------------- ارسال فرم ---------------- */
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (mode === 'register' && !agree) return toast.err('پذیرش قوانین الزامی است')
    setLoading(true)
    try {
      if (mode === 'login') {
        await login({ email: email.trim() || toEnDigits(mobile).trim(), password, otp })
      } else {
        await register({
          email: email.trim() || undefined, password,
          mobile: toEnDigits(mobile).trim(), ticket, referral: ref.trim(),
          first_name: firstName.trim(), last_name: lastName.trim(),
          national_id: toEnDigits(nationalId).trim(),
          birth_date: toEnDigits(birthDate).trim(),
        })
      }
      toast.ok(mode === 'login' ? 'خوش آمدید!' : 'حساب شما با موفقیت ساخته شد')
      nav(params.get('next') || '/dashboard', { replace: true })
    } catch (err: any) {
      const m = String(err.message)
      if (m.includes('دومرحله')) setNeedOtp(true)
      toast.err(err.message)
    } finally { setLoading(false) }
  }

  const title = mode === 'login' ? 'ورود به حساب کاربری' : 'ساخت حساب کاربری'

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
      <div className="pointer-events-none absolute inset-0 bg-grid-fade bg-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
      <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 animate-float rounded-full bg-brand-500/25 blur-[110px]" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 animate-float rounded-full bg-aqua/20 blur-[110px] [animation-delay:2s]" />

      <div className="relative w-full max-w-md">
        <div className="mb-7 flex flex-col items-center gap-4 text-center">
          <Logo />
          <div>
            <h1 className="text-2xl font-extrabold text-white">{title}</h1>
            <p className="mt-2 text-sm text-slate-400">
              {step === 'code'
                ? `کد ${fa(cfg.otpLength)} رقمی پیامک‌شده را وارد کنید`
                : mode === 'login'
                  ? 'برای ادامه، اطلاعات حساب خود را وارد کنید'
                  : 'در کمتر از یک دقیقه عضو ارزینو شوید'}
            </p>
          </div>
        </div>

        {/* ─────────── مرحله وارد کردن کد ─────────── */}
        {step === 'code' ? (
          <div className="card !p-6">
            <div className="mb-5 flex items-center justify-center gap-2 rounded-xl border border-brand-500/25 bg-brand-500/[.07] p-3 text-xs text-slate-200">
              <MessageSquare className="h-4 w-4 text-brand-300" />
              <span className="num" dir="ltr">{toEnDigits(mobile)}</span>
            </div>

            <CodeInput length={cfg.otpLength} value={code}
              onChange={setCode} onComplete={(v) => verifyCode(v)} />

            <div className="mt-5 flex items-center justify-between text-xs text-slate-400">
              {cooldown > 0 ? (
                <span>ارسال مجدد تا <Countdown seconds={cooldown} onDone={() => setCooldown(0)} /></span>
              ) : (
                <button onClick={requestCode} className="flex items-center gap-1.5 text-brand-300 hover:underline">
                  <RotateCcw className="h-3.5 w-3.5" /> ارسال مجدد کد
                </button>
              )}
              <button onClick={reset} className="text-slate-500 hover:text-slate-300">تغییر شماره</button>
            </div>

            <Button onClick={() => verifyCode()} loading={loading} className="mt-5 w-full !py-3.5"
              disabled={code.length < cfg.otpLength}>
              تأیید و ادامه <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        ) : (

        /* ─────────── فرم اصلی ─────────── */
        <form onSubmit={step === 'finish' || !smsMode ? submit : (e) => { e.preventDefault(); requestCode() }}
          className="card !p-6">

          {/* انتخاب روش ورود */}
          {mode === 'login' && cfg.passwordLogin && cfg.otpLogin && (
            <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
              {([['password', 'رمز عبور', Lock], ['otp', 'کد پیامکی', MessageSquare]] as const).map(
                ([k, l, Icon]) => (
                  <button key={k} type="button" onClick={() => setMethod(k)}
                    className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition ${
                      method === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                        : 'text-slate-400 hover:text-slate-200'}`}>
                    <Icon className="h-3.5 w-3.5" /> {l}
                  </button>
                ))}
            </div>
          )}

          {step === 'finish' && (
            <div className="mb-5 flex items-center gap-2 rounded-xl border border-up/25 bg-up/[.07] p-3 text-xs text-slate-200">
              <Check className="h-4 w-4 text-up" />
              شماره <span className="num" dir="ltr">{toEnDigits(mobile)}</span> تأیید شد؛ حالا حساب خود را کامل کنید.
            </div>
          )}

          {/* شماره موبایل */}
          {(smsMode || mode === 'register') && (
            <Field label="شماره موبایل"
              hint={mode === 'register' && !cfg.requireMobileVerify ? 'برای اطلاع‌رسانی واریز و برداشت' : undefined}>
              <div className="relative">
                <Smartphone className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input className="input num pr-10" dir="ltr" placeholder="09xxxxxxxxx"
                  required={smsMode} disabled={step === 'finish'}
                  value={mobile} onChange={(e) => setMobile(e.target.value)} />
              </div>
            </Field>
          )}

          {/* ایمیل */}
          {(!smsMode || step === 'finish') && (
            <Field label={mode === 'register' && cfg.requireMobileVerify ? 'ایمیل (اختیاری)' : 'ایمیل'}>
              <div className="relative">
                <Mail className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input className="input pr-10" dir="ltr" type="email"
                  required={mode === 'login' && !smsMode ? false : !cfg.requireMobileVerify}
                  placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </Field>
          )}

          {/* رمز عبور */}
          {(!smsMode || step === 'finish') && (
            <Field label="رمز عبور" hint={mode === 'register' ? 'حداقل ۶ کاراکتر شامل حرف و عدد' : undefined}>
              <div className="relative">
                <Lock className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input className="input px-10" dir="ltr" type={show ? 'text' : 'password'} required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-300">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
          )}

          {mode === 'register' && cfg.identityRequired && (
            <>
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-brand-500/25 bg-brand-500/[.06] p-3 text-[11px] leading-6 text-brand-200">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                مشخصات شما به‌صورت آنی از مراجع رسمی استعلام می‌شود و باید با هم
                بخوانند. شماره موبایل هم باید <b>به نام خودتان</b> ثبت شده باشد.
              </div>
              <div className="grid gap-0 sm:grid-cols-2 sm:gap-3">
                <Field label="نام">
                  <input className="input" value={firstName} autoComplete="given-name"
                    onChange={(e) => setFirstName(e.target.value)} placeholder="علی" />
                </Field>
                <Field label="نام خانوادگی">
                  <input className="input" value={lastName} autoComplete="family-name"
                    onChange={(e) => setLastName(e.target.value)} placeholder="رضایی" />
                </Field>
              </div>
              <div className="grid gap-0 sm:grid-cols-2 sm:gap-3">
                <Field label="کد ملی" hint="۱۰ رقم، مطابق کارت ملی">
                  <div className="relative">
                    <IdCard className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input className="input num pr-10" dir="ltr" inputMode="numeric" maxLength={10}
                      value={nationalId} placeholder="0012345678"
                      onChange={(e) => setNationalId(toEnDigits(e.target.value).replace(/\D/g, ''))} />
                  </div>
                </Field>
                {cfg.birthDateRequired !== false && (
                  <Field label="تاریخ تولد" hint="شمسی — مثلاً ۱۳۷۰/۰۱/۰۱">
                    <div className="relative">
                      <CalendarDays className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input className="input num pr-10" dir="ltr" inputMode="numeric" maxLength={10}
                        value={birthDate} placeholder="1370/01/01"
                        onChange={(e) => {
                          // خودکار اسلش می‌گذارد تا کاربر قالب را حدس نزند
                          const d = toEnDigits(e.target.value).replace(/\D/g, '').slice(0, 8)
                          setBirthDate(d.length > 6 ? `${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6)}`
                            : d.length > 4 ? `${d.slice(0, 4)}/${d.slice(4)}` : d)
                        }} />
                    </div>
                  </Field>
                )}
              </div>
            </>
          )}

          {mode === 'register' && (
            <Field label="کد معرف (اختیاری)">
              <div className="relative">
                <Gift className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input className="input pr-10" dir="ltr" placeholder="ARZINO01"
                  value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} />
              </div>
            </Field>
          )}

          {needOtp && (
            <Field label="کد تأیید دومرحله‌ای" hint="کد ۶ رقمی برنامه Authenticator">
              <div className="relative">
                <ShieldCheck className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input className="input num pr-10 tracking-[.4em]" dir="ltr" maxLength={6}
                  value={otp} onChange={(e) => setOtp(toEnDigits(e.target.value).replace(/\D/g, ''))} />
              </div>
            </Field>
          )}

          {mode === 'register' && (
            <label className="mb-4 flex cursor-pointer items-start gap-2.5 text-xs leading-6 text-slate-400">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                className="mt-1 h-4 w-4 accent-brand-500" />
              <span>
                <Link to="/about" className="text-brand-300 hover:underline">قوانین و مقررات</Link> ارزینو را می‌پذیرم
                و تأیید می‌کنم که مسئولیت معاملات بر عهده خودم است.
              </span>
            </label>
          )}

          <Button type="submit" loading={loading} className="w-full !py-3.5">
            {smsMode && step !== 'finish'
              ? <><KeyRound className="h-4 w-4" /> ارسال کد تأیید</>
              : <>{mode === 'login' ? 'ورود به حساب' : 'ثبت‌نام و شروع'} <ArrowLeft className="h-4 w-4" /></>}
          </Button>

          {mode === 'login' && !smsMode && cfg.otpLogin && (
            <button type="button" onClick={() => setMethod('otp')} className="btn-ghost mt-2 w-full text-xs">
              <MessageSquare className="h-3.5 w-3.5" /> ورود با کد پیامکی
            </button>
          )}

          <p className="mt-5 text-center text-sm text-slate-400">
            {mode === 'login' ? (
              <>حساب کاربری ندارید؟ <Link to="/register" className="font-semibold text-brand-300 hover:underline">ثبت‌نام کنید</Link></>
            ) : (
              <>قبلاً ثبت‌نام کرده‌اید؟ <Link to="/login" className="font-semibold text-brand-300 hover:underline">وارد شوید</Link></>
            )}
          </p>
        </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-600">
          ورود شما به معنای پذیرش <Link to="/about" className="link-muted">سیاست حریم خصوصی</Link> ارزینو است.
        </p>
      </div>
    </div>
  )
}
